// hooks/useFathomTracker.ts
import { useEffect, useState, useCallback, useRef } from 'react';
import fathomTracker from '@lib/fathom-tracker';

export interface UseFathomTrackerReturn {
  trackEvent: (event: string, value?: number) => void;
  isReady: boolean;
  queueLength: number;
}

export const useFathomTracker = (): UseFathomTrackerReturn => {
  const [isReady, setIsReady] = useState(fathomTracker.ready);
  const [queueLength, setQueueLength] = useState(fathomTracker.queueLength);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // If already ready, no need to subscribe
    if (fathomTracker.ready) {
      setIsReady(true);
      setQueueLength(0);
      return;
    }

    // Subscribe to ready state changes
    const unsubscribe = fathomTracker.subscribe(() => {
      setIsReady(true);
      setQueueLength(fathomTracker.queueLength);
    });

    unsubscribeRef.current = unsubscribe;

    // Update queue length periodically while waiting
    const queueUpdateInterval = setInterval(() => {
      if (!fathomTracker.ready) {
        setQueueLength(fathomTracker.queueLength);
      }
    }, 500);

    // Cleanup function
    return () => {
      unsubscribe();
      clearInterval(queueUpdateInterval);
      unsubscribeRef.current = null;
    };
  }, []);

  const trackEvent = useCallback((event: string, value?: number) => {
    fathomTracker.trackEvent(event, value);

    // Update queue length immediately after tracking
    if (!fathomTracker.ready) {
      setQueueLength(fathomTracker.queueLength);
    }
  }, []);

  return {
    trackEvent,
    isReady,
    queueLength,
  };
};
